/* Hardcore Printing — global behaviors. Vanilla JS, no dependencies. */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Mobile nav drawer ---------- */
  function initNav() {
    var toggle = document.querySelector('[data-nav-toggle]');
    var drawer = document.querySelector('[data-mobile-nav]');
    var overlay = document.querySelector('[data-nav-overlay]');
    if (!toggle || !drawer) return;

    var closeEls = document.querySelectorAll('[data-nav-close]');

    function open() {
      drawer.classList.add('is-open');
      if (overlay) overlay.classList.add('is-open');
      toggle.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
      var first = drawer.querySelector('a, button');
      if (first) first.focus();
    }
    function close() {
      drawer.classList.remove('is-open');
      if (overlay) overlay.classList.remove('is-open');
      toggle.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
    }
    function isOpen() { return drawer.classList.contains('is-open'); }

    toggle.addEventListener('click', function () { isOpen() ? close() : open(); });
    if (overlay) overlay.addEventListener('click', close);
    closeEls.forEach(function (el) { el.addEventListener('click', close); });
    drawer.querySelectorAll('a').forEach(function (a) { a.addEventListener('click', close); });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && isOpen()) { close(); toggle.focus(); } });
    window.addEventListener('resize', function () { if (window.innerWidth > 900 && isOpen()) close(); });
  }

  /* ---------- Header shadow on scroll ---------- */
  function initHeaderState() {
    var header = document.querySelector('[data-header]');
    if (!header) return;
    var onScroll = function () {
      if (window.scrollY > 12) header.setAttribute('data-scrolled', '');
      else header.removeAttribute('data-scrolled');
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* ---------- Scroll reveal ---------- */
  function initReveal() {
    var els = document.querySelectorAll('.reveal');
    if (!els.length) return;
    if (reduceMotion || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('is-in'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) { entry.target.classList.add('is-in'); io.unobserve(entry.target); }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });
    els.forEach(function (el) { io.observe(el); });
  }

  /* ---------- Marquee: duplicate content for seamless loop ---------- */
  function initMarquee() {
    document.querySelectorAll('[data-marquee]').forEach(function (track) {
      if (reduceMotion) return;
      var clone = track.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      track.parentNode.appendChild(clone);
    });
  }

  /* ---------- Auto-advancing carousel (Graphic Design gallery) ---------- */
  function initCarousels() {
    document.querySelectorAll('[data-carousel]').forEach(function (root) {
      var track = root.querySelector('[data-carousel-track]');
      if (!track) return;
      var slides = Array.prototype.slice.call(track.children);
      if (slides.length < 2) return;

      var interval = parseInt(root.getAttribute('data-interval'), 10) || 3500;
      var index = 0, timer = null, paused = false;

      function go(i) {
        index = (i + slides.length) % slides.length;
        var target = slides[index];
        track.scrollTo({ left: target.offsetLeft - track.offsetLeft, behavior: reduceMotion ? 'auto' : 'smooth' });
        root.querySelectorAll('[data-carousel-dot]').forEach(function (d, di) {
          d.setAttribute('aria-current', di === index ? 'true' : 'false');
        });
      }
      function next() { go(index + 1); }
      function start() { if (reduceMotion || paused) return; stop(); timer = setInterval(next, interval); }
      function stop() { if (timer) { clearInterval(timer); timer = null; } }

      root.querySelectorAll('[data-carousel-dot]').forEach(function (dot, di) {
        dot.addEventListener('click', function () { go(di); start(); });
      });
      var prevBtn = root.querySelector('[data-carousel-prev]');
      var nextBtn = root.querySelector('[data-carousel-next]');
      if (prevBtn) prevBtn.addEventListener('click', function () { go(index - 1); start(); });
      if (nextBtn) nextBtn.addEventListener('click', function () { go(index + 1); start(); });

      root.addEventListener('mouseenter', function () { paused = true; stop(); });
      root.addEventListener('mouseleave', function () { paused = false; start(); });
      root.addEventListener('focusin', function () { paused = true; stop(); });
      root.addEventListener('focusout', function () { paused = false; start(); });
      document.addEventListener('visibilitychange', function () { document.hidden ? stop() : start(); });

      go(0); start();
    });
  }

  /* ---------- FAQ accordion ---------- */
  function initAccordion() {
    document.querySelectorAll('[data-accordion-trigger]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var expanded = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', String(!expanded));
        var panel = document.getElementById(btn.getAttribute('aria-controls'));
        if (panel) panel.hidden = expanded;
      });
    });
  }

  /* ---------- Cursor spotlight (red gradient follows the mouse) ---------- */
  function initSpotlight() {
    // Skip on touch/coarse pointers — there is no hover to follow.
    if (window.matchMedia('(hover: none)').matches) return;
    document.querySelectorAll('[data-spotlight]').forEach(function (el) {
      var raf = null, px = 50, py = 50;
      el.addEventListener('pointermove', function (e) {
        if (e.pointerType === 'touch') return;
        var r = el.getBoundingClientRect();
        px = ((e.clientX - r.left) / r.width) * 100;
        py = ((e.clientY - r.top) / r.height) * 100;
        if (!raf) {
          raf = requestAnimationFrame(function () {
            el.style.setProperty('--spot-x', px + '%');
            el.style.setProperty('--spot-y', py + '%');
            raf = null;
          });
        }
      });
      el.addEventListener('pointerenter', function () { el.classList.add('is-hovering'); });
      el.addEventListener('pointerleave', function () { el.classList.remove('is-hovering'); });
    });
  }

  /* ---------- Simple client-side form UX (disable on submit) ---------- */
  function initForms() {
    document.querySelectorAll('form[data-form]').forEach(function (form) {
      form.addEventListener('submit', function () {
        var btn = form.querySelector('button[type="submit"]');
        if (btn) { btn.disabled = true; btn.dataset.label = btn.textContent; btn.textContent = 'Sending…'; }
      });
    });
  }

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  ready(function () {
    initNav();
    initHeaderState();
    initReveal();
    initMarquee();
    initCarousels();
    initAccordion();
    initSpotlight();
    initForms();
  });
})();
